import { useState, useRef, useEffect, useMemo } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Popup,
  ZoomControl,
  useMap,
} from "react-leaflet";
import {
  X,
  AlertCircle,
  RefreshCw,
  Calendar,
  MapPin,
  Play,
  Pause,
  RotateCcw,
  Navigation,
} from "lucide-react";
import L from "leaflet";
import { fetchTripHistory } from "../../services/fleetx_service";
import "leaflet/dist/leaflet.css";
import "./TripHistoryModal.css";

const BASE_DURATION_MS = 45000;
const SPEED_OPTIONS = [0.5, 1, 1.5, 2];
const CAR_DIRECTION_OFFSET = 180;
const HEADING_CHANGE_LIMIT = 100;

function toEpochMillis(value) {
  return new Date(value).getTime();
}

function parseDate(value) {
  if (!value) return null;

  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

function fmtTripDate(value) {
  const date = parseDate(value);

  if (!date) {
    return value || "—";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtTripDateShort(value) {
  const date = parseDate(value);

  if (!date) {
    return value || "—";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDuration(ms) {
  if (
    ms === undefined ||
    ms === null ||
    Number.isNaN(Number(ms))
  ) {
    return "—";
  }

  const totalSec = Math.max(
    0,
    Math.round(Number(ms) / 1000)
  );

  const hours = Math.floor(
    totalSec / 3600
  );

  const minutes = Math.floor(
    (totalSec % 3600) / 60
  );

  const seconds = totalSec % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
}

function fmtDistance(km) {
  if (
    km === undefined ||
    km === null ||
    Number.isNaN(Number(km))
  ) {
    return "—";
  }

  return `${Number(km).toFixed(1)} km`;
}

function validPoint(lat, lon) {
  return (
    typeof lat === "number" &&
    typeof lon === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lon) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lon) <= 180
  );
}

function normalizeAddress(address) {
  if (
    address === undefined ||
    address === null
  ) {
    return "";
  }

  return String(address)
    .trim()
    .replace(/\s+/g, " ")
    .replace(/,(?!\s)/g, ", ");
}

function fmtCoordinates(lat, lon) {
  if (!validPoint(lat, lon)) {
    return "";
  }

  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

function fmtLocation(address, lat, lon) {
  const cleanAddress =
    normalizeAddress(address);

  if (cleanAddress) {
    return cleanAddress;
  }

  return (
    fmtCoordinates(lat, lon) ||
    "—"
  );
}

function normalizeHeading(value) {
  return (
    ((Number(value) % 360) + 360) %
    360
  );
}

function shortestHeadingDelta(
  from,
  to
) {
  return (
    ((to - from + 540) % 360) -
    180
  );
}

function blendHeading(
  from,
  to,
  amount
) {
  return normalizeHeading(
    from +
      shortestHeadingDelta(
        from,
        to
      ) *
        amount
  );
}

function bearingDeg(
  [lat1, lon1],
  [lat2, lon2]
) {
  const toRad = (value) =>
    (value * Math.PI) / 180;

  const y =
    Math.sin(
      toRad(lon2 - lon1)
    ) *
    Math.cos(toRad(lat2));

  const x =
    Math.cos(toRad(lat1)) *
      Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.cos(
        toRad(lon2 - lon1)
      );

  return normalizeHeading(
    (Math.atan2(y, x) * 180) /
      Math.PI
  );
}

function haversineKm(
  [lat1, lon1],
  [lat2, lon2]
) {
  const earthRadius = 6371;

  const toRad = (value) =>
    (value * Math.PI) / 180;

  const dLat = toRad(
    lat2 - lat1
  );

  const dLon = toRad(
    lon2 - lon1
  );

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;

  return (
    earthRadius *
    2 *
    Math.asin(Math.sqrt(a))
  );
}

function cleanRoutePoints(points) {
  const cleaned = [];

  points.forEach((point) => {
    if (!point || point.length !== 2) {
      return;
    }

    if (
      !validPoint(
        point[0],
        point[1]
      )
    ) {
      return;
    }

    const previous =
      cleaned[cleaned.length - 1];

    if (!previous) {
      cleaned.push(point);
      return;
    }

    const distance =
      haversineKm(
        previous,
        point
      );

    if (distance >= 0.00001) {
      cleaned.push(point);
    }
  });

  return cleaned;
}

function buildPathMeta(points) {
  const cleanPoints =
    cleanRoutePoints(points);

  if (cleanPoints.length < 2) {
    return {
      points: cleanPoints,
      cumulative: [0],
      total: 0,
    };
  }

  const cumulative = [0];

  for (
    let i = 1;
    i < cleanPoints.length;
    i += 1
  ) {
    cumulative.push(
      cumulative[i - 1] +
        haversineKm(
          cleanPoints[i - 1],
          cleanPoints[i]
        )
    );
  }

  return {
    points: cleanPoints,
    cumulative,
    total:
      cumulative[
        cumulative.length - 1
      ],
  };
}

function stableBearing(
  points,
  index,
  previousHeading
) {
  if (points.length < 2) {
    return previousHeading || 0;
  }

  const current =
    points[index];

  let candidateIndex =
    Math.min(
      index + 1,
      points.length - 1
    );

  let candidate =
    points[candidateIndex];

  let distance =
    haversineKm(
      current,
      candidate
    );

  if (
    distance < 0.002 &&
    index + 2 < points.length
  ) {
    candidateIndex =
      index + 2;

    candidate =
      points[candidateIndex];

    distance =
      haversineKm(
        current,
        candidate
      );
  }

  if (distance < 0.002) {
    return (
      previousHeading ||
      (index > 0
        ? bearingDeg(
            points[index - 1],
            current
          )
        : 0)
    );
  }

  const candidateHeading =
    bearingDeg(
      current,
      candidate
    );

  if (
    previousHeading ===
      undefined ||
    previousHeading === null
  ) {
    return candidateHeading;
  }

  const delta = Math.abs(
    shortestHeadingDelta(
      previousHeading,
      candidateHeading
    )
  );

  if (
    delta >
    HEADING_CHANGE_LIMIT
  ) {
    return previousHeading;
  }

  return blendHeading(
    previousHeading,
    candidateHeading,
    0.2
  );
}

function pointAtProgress(
  meta,
  progress,
  previousHeading
) {
  const {
    points,
    cumulative,
    total,
  } = meta;

  if (!points.length) {
    return {
      position: null,
      bearing: previousHeading || 0,
    };
  }

  if (
    points.length === 1 ||
    total <= 0
  ) {
    return {
      position: points[0],
      bearing:
        previousHeading ||
        0,
    };
  }

  const safeProgress =
    Math.min(
      1,
      Math.max(0, progress)
    );

  const target =
    safeProgress * total;

  let index = 0;

  while (
    index <
      cumulative.length - 2 &&
    cumulative[index + 1] <
      target
  ) {
    index += 1;
  }

  const start = points[index];
  const end = points[index + 1];

  const startDistance =
    cumulative[index];

  const endDistance =
    cumulative[index + 1];

  const segmentDistance =
    endDistance -
    startDistance;

  const localProgress =
    segmentDistance > 0
      ? (target -
          startDistance) /
        segmentDistance
      : 0;

  const latitude =
    start[0] +
    (end[0] - start[0]) *
      localProgress;

  const longitude =
    start[1] +
    (end[1] - start[1]) *
      localProgress;

  const bearing =
    stableBearing(
      points,
      index,
      previousHeading
    );

  return {
    position: [
      latitude,
      longitude,
    ],
    bearing,
  };
}

function buildCarIcon(
  rotation = 0,
  active = false
) {
  return L.divIcon({
    className:
      "th-car-marker-icon",
    html: `
      <div
        class="th-car-marker"
        style="--th-car-rotation:${rotation}deg;"
      >
        ${
          active
            ? '<div class="th-car-pulse"></div>'
            : ""
        }

        <img
          src="/assets/carmap.png"
          alt="Vehicle"
          class="th-car-image"
        />
      </div>
    `,
    iconSize: [70, 70],
    iconAnchor: [35, 35],
  });
}

const startIcon = L.divIcon({
  className:
    "th-location-marker-icon",
  html: `
    <div class="th-pin th-pin-start">
      <div class="th-pin-dot"></div>
    </div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const endIcon = L.divIcon({
  className:
    "th-location-marker-icon",
  html: `
    <div class="th-pin th-pin-end">
      <div class="th-pin-dot"></div>
    </div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function MapViewport({
  points,
}) {
  const map = useMap();
  const fittedKeyRef =
    useRef("");

  useEffect(() => {
    if (!points.length) {
      return;
    }

    const key = points
      .map(
        ([lat, lon]) =>
          `${lat.toFixed(
            6
          )},${lon.toFixed(6)}`
      )
      .join("|");

    if (
      fittedKeyRef.current === key
    ) {
      map.invalidateSize();
      return;
    }

    fittedKeyRef.current = key;

    const frame =
      window.requestAnimationFrame(
        () => {
          map.invalidateSize();

          if (
            points.length === 1
          ) {
            map.setView(
              points[0],
              16,
              {
                animate: false,
              }
            );
            return;
          }

          map.fitBounds(
            L.latLngBounds(
              points
            ),
            {
              padding: [45, 45],
              maxZoom: 16,
              animate: false,
            }
          );
        }
      );

    return () =>
      window.cancelAnimationFrame(
        frame
      );
  }, [map, points]);

  return null;
}

export default function TripHistoryModal({
  vehicleNumber,
  apiBaseUrl,
  getToken,
  onClose,
}) {
  const [fromDate, setFromDate] =
    useState("");

  const [toDate, setToDate] =
    useState("");

  const [trips, setTrips] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [routeLoading, setRouteLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [routeError, setRouteError] =
    useState(null);

  const [routeSets, setRouteSets] =
    useState([]);

  const [isPlaying, setIsPlaying] =
    useState(false);

  const [progress, setProgress] =
    useState(0);

  const [speed, setSpeed] =
    useState(1);

  const [carPosition, setCarPosition] =
    useState(null);

  const [carBearing, setCarBearing] =
    useState(0);

  const mapRef = useRef(null);
  const carMarkerRef = useRef(null);
  const animationFrameRef =
    useRef(null);

  const progressRef = useRef(0);
  const routeSegmentsRef =
    useRef([]);

  const totalDistanceRef =
    useRef(0);

  const speedRef =
    useRef(speed);

  const headingRef =
    useRef(0);

  const lastFrameRef =
    useRef(null);

  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const tripList = useMemo(() => {
    return (Array.isArray(trips)
      ? [...trips]
      : []
    ).sort(
      (a, b) =>
        new Date(a.start) -
        new Date(b.start)
    );
  }, [trips]);

  const tripCoordinates = useMemo(
    () =>
      tripList
        .map((trip) => {
          if (
            !validPoint(
              trip.start_lat,
              trip.start_lon
            ) ||
            !validPoint(
              trip.end_lat,
              trip.end_lon
            )
          ) {
            return null;
          }

          return {
            trip,
            start: [
              Number(trip.start_lat),
              Number(trip.start_lon),
            ],
            end: [
              Number(trip.end_lat),
              Number(trip.end_lon),
            ],
          };
        })
        .filter(Boolean),
    [tripList]
  );

  async function handleFetch() {
    if (
      !fromDate ||
      !toDate
    ) {
      setError(
        "Pick both a start and end date/time."
      );
      return;
    }

    const fromMs =
      toEpochMillis(fromDate);

    const toMs =
      toEpochMillis(toDate);

    if (
      Number.isNaN(fromMs) ||
      Number.isNaN(toMs)
    ) {
      setError(
        "Invalid date/time values."
      );
      return;
    }

    if (fromMs >= toMs) {
      setError(
        "'From' must be earlier than 'To'."
      );
      return;
    }

    if (
      animationFrameRef.current
    ) {
      window.cancelAnimationFrame(
        animationFrameRef.current
      );
      animationFrameRef.current =
        null;
    }

    setLoading(true);
    setRouteLoading(false);
    setError(null);
    setRouteError(null);
    setRouteSets([]);
    setIsPlaying(false);
    setTrips(null);

    progressRef.current = 0;
    setProgress(0);
    setCarPosition(null);
    setCarBearing(0);
    headingRef.current = 0;

    try {
      const data =
        await fetchTripHistory(
          apiBaseUrl,
          getToken,
          vehicleNumber,
          fromMs,
          toMs
        );

      setTrips(
        Array.isArray(data)
          ? data
          : []
      );
    } catch (e) {
      setError(
        e?.message ||
          "Unable to load trip history."
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadRoutes() {
      if (
        !tripCoordinates.length
      ) {
        setRouteSets([]);
        setRouteLoading(false);
        return;
      }

      setRouteLoading(true);
      setRouteError(null);

      const results =
        await Promise.all(
          tripCoordinates.map(
            async ({
              trip,
              start,
              end,
            }) => {
              const fallback = {
                trip,
                points: [
                  start,
                  end,
                ],
                routed: false,
              };

              try {
                const endpoint =
                  "https://router.project-osrm.org/route/v1/driving/" +
                  `${start[1]},${start[0]};${end[1]},${end[0]}` +
                  "?overview=full&geometries=geojson&steps=false";

                const response =
                  await fetch(
                    endpoint
                  );

                if (
                  !response.ok
                ) {
                  return fallback;
                }

                const data =
                  await response.json();

                const coordinates =
                  data?.routes?.[0]
                    ?.geometry
                    ?.coordinates;

                if (
                  !Array.isArray(
                    coordinates
                  ) ||
                  coordinates.length < 2
                ) {
                  return fallback;
                }

                const points =
                  cleanRoutePoints(
                    coordinates
                      .map(
                        ([lon, lat]) => [
                          Number(lat),
                          Number(lon),
                        ]
                      )
                  );

                if (
                  points.length < 2
                ) {
                  return fallback;
                }

                return {
                  trip,
                  points,
                  routed: true,
                };
              } catch {
                return fallback;
              }
            }
          )
        );

      if (cancelled) {
        return;
      }

      setRouteSets(results);
      setRouteLoading(false);

      const fallbackCount =
        results.filter(
          (item) =>
            !item.routed
        ).length;

      if (
        fallbackCount > 0
      ) {
        setRouteError(
          `${fallbackCount} route${
            fallbackCount === 1
              ? ""
              : "s"
          } could not be road-routed; GPS points are shown instead.`
        );
      }
    }

    loadRoutes();

    return () => {
      cancelled = true;
    };
  }, [tripCoordinates]);

  const renderedRouteSets =
    routeSets.length > 0
      ? routeSets
      : tripCoordinates.map(
          ({
            trip,
            start,
            end,
          }) => ({
            trip,
            points: [
              start,
              end,
            ],
            routed: false,
          })
        );

  const mapPoints = useMemo(() => {
    const unique = [];
    const seen = new Set();

    renderedRouteSets.forEach(
      (route) => {
        route.points.forEach(
          ([lat, lon]) => {
            const key =
              `${lat.toFixed(
                6
              )},${lon.toFixed(6)}`;

            if (!seen.has(key)) {
              seen.add(key);
              unique.push([
                lat,
                lon,
              ]);
            }
          }
        );
      }
    );

    return unique;
  }, [renderedRouteSets]);

  const animationSegments =
    useMemo(() => {
      return renderedRouteSets
        .map((route) => ({
          trip: route.trip,
          points: route.points,
          meta: buildPathMeta(
            route.points
          ),
        }))
        .filter(
          (segment) =>
            segment.meta.points
              .length >= 2 &&
            segment.meta.total > 0
        );
    }, [renderedRouteSets]);

  const totalAnimationDistance =
    useMemo(
      () =>
        animationSegments.reduce(
          (sum, segment) =>
            sum +
            segment.meta.total,
          0
        ),
      [animationSegments]
    );

  const hasRoute =
    animationSegments.length > 0 &&
    totalAnimationDistance > 0;

  useEffect(() => {
    routeSegmentsRef.current =
      animationSegments;

    totalDistanceRef.current =
      totalAnimationDistance;

    if (!hasRoute) {
      progressRef.current = 0;
      setProgress(0);
      setCarPosition(null);
      setCarBearing(0);
      headingRef.current = 0;
      return;
    }

    const first =
      animationSegments[0];

    const initial =
      pointAtProgress(
        first.meta,
        0,
        undefined
      );

    if (initial.position) {
      const heading =
        normalizeHeading(
          initial.bearing +
            CAR_DIRECTION_OFFSET
        );

      headingRef.current =
        heading;

      setCarPosition(
        initial.position
      );

      setCarBearing(heading);
    }
  }, [
    animationSegments,
    hasRoute,
    totalAnimationDistance,
  ]);

  useEffect(() => {
    if (
      !isPlaying ||
      !hasRoute
    ) {
      if (
        animationFrameRef.current
      ) {
        window.cancelAnimationFrame(
          animationFrameRef.current
        );

        animationFrameRef.current =
          null;
      }

      lastFrameRef.current =
        null;

      return undefined;
    }

    let cancelled = false;

    progressRef.current =
      progressRef.current >= 1
        ? 0
        : progressRef.current;

    if (progressRef.current === 0) {
      const first =
        routeSegmentsRef.current[0];

      if (first) {
        const initial =
          pointAtProgress(
            first.meta,
            0,
            undefined
          );

        if (
          initial.position
        ) {
          const heading =
            normalizeHeading(
              initial.bearing +
                CAR_DIRECTION_OFFSET
            );

          headingRef.current =
            heading;

          setCarPosition(
            initial.position
          );

          setCarBearing(
            heading
          );
        }
      }
    }

    lastFrameRef.current =
      null;

    function calculateState(
      globalProgress
    ) {
      const segments =
        routeSegmentsRef.current;

      const totalDistance =
        totalDistanceRef.current;

      if (
        !segments.length ||
        totalDistance <= 0
      ) {
        return {
          position: null,
          bearing: headingRef.current,
        };
      }

      const targetDistance =
        Math.min(
          1,
          Math.max(
            0,
            globalProgress
          )
        ) *
        totalDistance;

      let accumulated = 0;

      let previousHeading =
        headingRef.current;

      for (
        let index = 0;
        index < segments.length;
        index += 1
      ) {
        const segment =
          segments[index];

        const next =
          accumulated +
          segment.meta.total;

        if (
          targetDistance <= next ||
          index ===
            segments.length - 1
        ) {
          const localDistance =
            Math.min(
              segment.meta.total,
              Math.max(
                0,
                targetDistance -
                  accumulated
              )
            );

          const localProgress =
            segment.meta.total > 0
              ? localDistance /
                segment.meta.total
              : 0;

          return pointAtProgress(
            segment.meta,
            localProgress,
            previousHeading
          );
        }

        const endState =
          pointAtProgress(
            segment.meta,
            1,
            previousHeading
          );

        previousHeading =
          endState.bearing;

        accumulated = next;
      }

      return {
        position: null,
        bearing: previousHeading,
      };
    }

    function animate(timestamp) {
      if (cancelled) {
        return;
      }

      if (
        lastFrameRef.current ===
        null
      ) {
        lastFrameRef.current =
          timestamp;
      }

      const deltaMs =
        Math.min(
          50,
          Math.max(
            0,
            timestamp -
              lastFrameRef.current
          )
        );

      lastFrameRef.current =
        timestamp;

      const duration =
        BASE_DURATION_MS /
        Math.max(
          1,
          speedRef.current
        );

      const nextProgress =
        Math.min(
          1,
          progressRef.current +
            deltaMs / duration
        );

      progressRef.current =
        nextProgress;

      const nextState =
        calculateState(
          nextProgress
        );

      if (
        nextState.position
      ) {
        const targetHeading =
          normalizeHeading(
            nextState.bearing +
              CAR_DIRECTION_OFFSET
          );

        const delta =
          shortestHeadingDelta(
            headingRef.current,
            targetHeading
          );

        const safeDelta =
          Math.max(
            -28,
            Math.min(28, delta)
          );

        const nextHeading =
          normalizeHeading(
            headingRef.current +
              safeDelta *
                (1 -
                  Math.exp(
                    -deltaMs /
                      220
                  ))
          );

        headingRef.current =
          nextHeading;

        setCarBearing(
          nextHeading
        );

        if (carMarkerRef.current) {
          carMarkerRef.current.setLatLng(
            nextState.position
          );
        } else {
          setCarPosition(
            nextState.position
          );
        }
      }

      setProgress(
        nextProgress
      );

      if (
        nextProgress < 1
      ) {
        animationFrameRef.current =
          window.requestAnimationFrame(
            animate
          );
      } else {
        animationFrameRef.current =
          null;

        lastFrameRef.current =
          null;

        setIsPlaying(false);
      }
    }

    animationFrameRef.current =
      window.requestAnimationFrame(
        animate
      );

    return () => {
      cancelled = true;

      if (
        animationFrameRef.current
      ) {
        window.cancelAnimationFrame(
          animationFrameRef.current
        );

        animationFrameRef.current =
          null;
      }

      lastFrameRef.current =
        null;
    };
  }, [hasRoute, isPlaying]);

  const carIcon = useMemo(
    () =>
      buildCarIcon(
        carBearing,
        isPlaying
      ),
    [carBearing, isPlaying]
  );

  function handleMapReady(map) {
    mapRef.current = map;

    window.setTimeout(() => {
      map.invalidateSize();
    }, 100);
  }

  function getStateFromProgress(
    value
  ) {
    const segments =
      routeSegmentsRef.current;

    const totalDistance =
      totalDistanceRef.current;

    if (
      !segments.length ||
      totalDistance <= 0
    ) {
      return null;
    }

    const targetDistance =
      Math.min(
        1,
        Math.max(0, value)
      ) * totalDistance;

    let accumulated = 0;
    let previousHeading =
      headingRef.current;

    for (
      let index = 0;
      index < segments.length;
      index += 1
    ) {
      const segment =
        segments[index];

      const next =
        accumulated +
        segment.meta.total;

      if (
        targetDistance <= next ||
        index ===
          segments.length - 1
      ) {
        const localDistance =
          Math.min(
            segment.meta.total,
            Math.max(
              0,
              targetDistance -
                accumulated
            )
          );

        const localProgress =
          segment.meta.total > 0
            ? localDistance /
              segment.meta.total
            : 0;

        return pointAtProgress(
          segment.meta,
          localProgress,
          previousHeading
        );
      }

      previousHeading =
        pointAtProgress(
          segment.meta,
          1,
          previousHeading
        ).bearing;

      accumulated = next;
    }

    return null;
  }

  function handlePlayToggle() {
    if (!hasRoute) {
      return;
    }

    if (
      progressRef.current >= 1
    ) {
      progressRef.current = 0;
      setProgress(0);

      const initial =
        getStateFromProgress(0);

      if (initial?.position) {
        const heading =
          normalizeHeading(
            initial.bearing +
              CAR_DIRECTION_OFFSET
          );

        headingRef.current =
          heading;

        setCarPosition(
          initial.position
        );

        setCarBearing(
          heading
        );
      }
    }

    lastFrameRef.current =
      null;

    setIsPlaying(
      (previous) => !previous
    );
  }

  function handleRestart() {
    setIsPlaying(false);

    progressRef.current = 0;
    setProgress(0);

    const initial =
      getStateFromProgress(0);

    if (initial?.position) {
      const heading =
        normalizeHeading(
          initial.bearing +
            CAR_DIRECTION_OFFSET
        );

      headingRef.current =
        heading;

      setCarPosition(
        initial.position
      );

      setCarBearing(heading);
    }
  }

  function handleScrub(
    event
  ) {
    setIsPlaying(false);

    const nextProgress =
      Number(
        event.target.value
      ) / 1000;

    progressRef.current =
      nextProgress;

    setProgress(
      nextProgress
    );

    const nextState =
      getStateFromProgress(
        nextProgress
      );

    if (
      nextState?.position
    ) {
      const heading =
        normalizeHeading(
          nextState.bearing +
            CAR_DIRECTION_OFFSET
        );

      headingRef.current =
        heading;

      setCarPosition(
        nextState.position
      );

      setCarBearing(heading);
    }
  }

  const fallbackCenter =
    mapPoints[0] || null;

  return (
    <div
      className="th-overlay"
      onClick={onClose}
    >
      <div
        className="th-card"
        onClick={(event) =>
          event.stopPropagation()
        }
      >
        <div className="th-header">
          <div className="th-header-main">
            <div className="th-header-title-row">
              <h3 className="th-title">
                {vehicleNumber}
              </h3>

              <span className="th-live-tag">
                GPS HISTORY
              </span>
            </div>

            <p className="th-subtitle">
              Trip History
            </p>
          </div>

          <button
            type="button"
            className="th-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="th-toolbar">
          <label className="th-date-field">
            <Calendar size={13} />
            <span>From</span>

            <input
              type="datetime-local"
              value={fromDate}
              onChange={(event) =>
                setFromDate(
                  event.target.value
                )
              }
            />
          </label>

          <label className="th-date-field">
            <Calendar size={13} />
            <span>To</span>

            <input
              type="datetime-local"
              value={toDate}
              onChange={(event) =>
                setToDate(
                  event.target.value
                )
              }
            />
          </label>

          <button
            type="button"
            className="th-fetch-btn"
            onClick={
              handleFetch
            }
            disabled={loading}
          >
            <RefreshCw
              size={15}
              className={
                loading
                  ? "th-spin"
                  : ""
              }
            />

            {loading
              ? "Loading…"
              : "Fetch"}
          </button>
        </div>

        {error && (
          <div className="th-error-banner">
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

        {routeError &&
          !error && (
            <div className="th-route-note">
              <Navigation size={14} />
              <span>
                {routeError}
              </span>
            </div>
          )}

        {loading && (
          <div className="th-loading-state">
            <RefreshCw
              size={16}
              className="th-spin"
            />
            <span>
              Loading trip history…
            </span>
          </div>
        )}

        {!loading &&
          tripList.length > 0 && (
            <div className="th-summary-row">
              <div className="th-summary-item">
                <span>Trips</span>
                <strong>
                  {tripList.length}
                </strong>
              </div>

              <div className="th-summary-item">
                <span>
                  Total distance
                </span>
                <strong>
                  {fmtDistance(
                    tripList.reduce(
                      (sum, trip) =>
                        sum +
                        (Number(
                          trip.distance_km
                        ) || 0),
                      0
                    )
                  )}
                </strong>
              </div>

              <div className="th-summary-item">
                <span>Route</span>
                <strong>
                  {routeLoading
                    ? "Building roads…"
                    : renderedRouteSets.some(
                          (item) =>
                            item.routed
                        )
                      ? "Road route"
                      : "GPS points"}
                </strong>
              </div>
            </div>
          )}

        {!loading &&
          hasRoute && (
            <>
              <div className="th-map-section">
                <div className="th-map-toolbar">
                  <div className="th-map-title">
                    <Navigation size={14} />
                    <span>
                      Vehicle movement
                    </span>
                  </div>

                  <div className="th-map-legend">
                    <span className="th-legend-item">
                      <i className="th-legend-start"></i>
                      Start
                    </span>

                    <span className="th-legend-item">
                      <i className="th-legend-end"></i>
                      End
                    </span>
                  </div>
                </div>

                <div className="th-map-canvas">
                  <MapContainer
                    className="th-gmap"
                    center={
                      fallbackCenter || [
                        20.5937,
                        78.9629,
                      ]
                    }
                    zoom={14}
                    minZoom={5}
                    maxZoom={19}
                    scrollWheelZoom
                    doubleClickZoom
                    dragging
                    touchZoom
                    zoomControl={false}
                    whenReady={(event) =>
                      handleMapReady(
                        event.target
                      )
                    }
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      maxZoom={19}
                      maxNativeZoom={19}
                      updateWhenIdle
                      updateWhenZooming={false}
                      keepBuffer={4}
                    />

                    <ZoomControl
                      position="bottomright"
                    />

                    <MapViewport
                      points={mapPoints}
                    />

                    {renderedRouteSets.map(
                      (
                        route,
                        index
                      ) => (
                        <Polyline
                          key={
                            route.trip
                              ?.trip_id ??
                            `route-${index}`
                          }
                          positions={
                            route.points
                          }
                          pathOptions={{
                            color:
                              "#178A4C",
                            weight: 5,
                            opacity: 0.84,
                            lineCap:
                              "round",
                            lineJoin:
                              "round",
                          }}
                        />
                      )
                    )}

                    {renderedRouteSets.map(
                      (
                        route,
                        index
                      ) => (
                        <Marker
                          key={`start-${route.trip?.trip_id ?? index}`}
                          position={
                            route.points[0]
                          }
                          icon={
                            startIcon
                          }
                        >
                          <Popup>
                            <div className="th-map-popup">
                              <strong>
                                Start
                              </strong>

                              <span>
                                {fmtLocation(
                                  route
                                    .trip
                                    ?.start_address,
                                  route
                                    .trip
                                    ?.start_lat,
                                  route
                                    .trip
                                    ?.start_lon
                                )}
                              </span>

                              <small>
                                {fmtCoordinates(
                                  route
                                    .trip
                                    ?.start_lat,
                                  route
                                    .trip
                                    ?.start_lon
                                )}
                              </small>
                            </div>
                          </Popup>
                        </Marker>
                      )
                    )}

                    {renderedRouteSets.map(
                      (
                        route,
                        index
                      ) => (
                        <Marker
                          key={`end-${route.trip?.trip_id ?? index}`}
                          position={
                            route.points[
                              route.points
                                .length - 1
                            ]
                          }
                          icon={
                            endIcon
                          }
                        >
                          <Popup>
                            <div className="th-map-popup">
                              <strong>
                                End
                              </strong>

                              <span>
                                {fmtLocation(
                                  route
                                    .trip
                                    ?.end_address,
                                  route
                                    .trip
                                    ?.end_lat,
                                  route
                                    .trip
                                    ?.end_lon
                                )}
                              </span>

                              <small>
                                {fmtCoordinates(
                                  route
                                    .trip
                                    ?.end_lat,
                                  route
                                    .trip
                                    ?.end_lon
                                )}
                              </small>
                            </div>
                          </Popup>
                        </Marker>
                      )
                    )}

                    {carPosition && (
                      <Marker
                        ref={carMarkerRef}
                        position={
                          carPosition
                        }
                        icon={carIcon}
                        zIndexOffset={
                          2000
                        }
                      />
                    )}
                  </MapContainer>

                  {routeLoading && (
                    <div className="th-map-loading">
                      <RefreshCw
                        size={14}
                        className="th-spin"
                      />
                      <span>
                        Building road route…
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="th-playback-bar">
                <button
                  type="button"
                  className="th-play-btn"
                  onClick={
                    handlePlayToggle
                  }
                  aria-label={
                    isPlaying
                      ? "Pause"
                      : "Play"
                  }
                  disabled={!hasRoute}
                >
                  {isPlaying ? (
                    <Pause size={15} />
                  ) : (
                    <Play size={15} />
                  )}
                </button>

                <button
                  type="button"
                  className="th-restart-btn"
                  onClick={
                    handleRestart
                  }
                  aria-label="Restart"
                  disabled={
                    progress === 0 &&
                    !isPlaying
                  }
                >
                  <RotateCcw size={14} />
                </button>

                <div className="th-progress-wrap">
                  <input
                    className="th-scrub"
                    type="range"
                    min={0}
                    max={1000}
                    value={Math.round(
                      progress * 1000
                    )}
                    onChange={
                      handleScrub
                    }
                    style={{
                      "--th-progress": `${
                        progress * 100
                      }%`,
                    }}
                  />
                </div>

                <div className="th-speed-group">
                  {SPEED_OPTIONS.map(
                    (option) => (
                      <button
                        type="button"
                        key={option}
                        className={`th-speed-btn ${
                          speed ===
                          option
                            ? "th-speed-btn-active"
                            : ""
                        }`}
                        onClick={() =>
                          setSpeed(
                            option
                          )
                        }
                      >
                        {option}x
                      </button>
                    )
                  )}
                </div>
              </div>
            </>
          )}

        {!loading &&
          tripList.length > 0 && (
            <div className="th-table-scroll">
              <table className="th-data-table">
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>
                      Start Location
                    </th>
                    <th>End</th>
                    <th>
                      End Location
                    </th>
                    <th title="Distance">
                      Dist.
                    </th>
                    <th title="Duration">
                      Dur.
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {tripList.map(
                    (trip, index) => {
                      const startAddress =
                        fmtLocation(
                          trip.start_address,
                          trip.start_lat,
                          trip.start_lon
                        );

                      const endAddress =
                        fmtLocation(
                          trip.end_address,
                          trip.end_lat,
                          trip.end_lon
                        );

                      return (
                        <tr
                          key={
                            trip.trip_id ??
                            `trip-${index}`
                          }
                        >
                          <td
                            className="th-mono"
                            data-label="Start"
                            title={fmtTripDate(
                              trip.start
                            )}
                          >
                            {fmtTripDateShort(
                              trip.start
                            )}
                          </td>

                          <td
                            className="th-location-cell"
                            data-label="Start Location"
                            title={
                              startAddress
                            }
                          >
                            <span className="th-location-primary">
                              {startAddress}
                            </span>

                            {fmtCoordinates(
                              trip.start_lat,
                              trip.start_lon
                            ) && (
                              <span className="th-location-coordinates">
                                {fmtCoordinates(
                                  trip.start_lat,
                                  trip.start_lon
                                )}
                              </span>
                            )}
                          </td>

                          <td
                            className="th-mono"
                            data-label="End"
                            title={fmtTripDate(
                              trip.end
                            )}
                          >
                            {fmtTripDateShort(
                              trip.end
                            )}
                          </td>

                          <td
                            className="th-location-cell"
                            data-label="End Location"
                            title={
                              endAddress
                            }
                          >
                            <span className="th-location-primary">
                              {endAddress}
                            </span>

                            {fmtCoordinates(
                              trip.end_lat,
                              trip.end_lon
                            ) && (
                              <span className="th-location-coordinates">
                                {fmtCoordinates(
                                  trip.end_lat,
                                  trip.end_lon
                                )}
                              </span>
                            )}
                          </td>

                          <td
                            data-label="Distance"
                            title={fmtDistance(
                              trip.distance_km
                            )}
                          >
                            {fmtDistance(
                              trip.distance_km
                            )}
                          </td>

                          <td
                            data-label="Duration"
                            title={fmtDuration(
                              trip.duration_ms
                            )}
                          >
                            {fmtDuration(
                              trip.duration_ms
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          )}

        {!loading &&
          trips &&
          tripList.length === 0 &&
          !error && (
            <div className="th-empty-state">
              <MapPin size={16} />
              <span>
                No trips found in this
                date range.
              </span>
            </div>
          )}
      </div>
    </div>
  );
}